import React, { useCallback, useContext, useMemo } from "react";
import { commitMutation, graphql } from "react-relay";
import { useHistory } from "react-router";

import {
	faArchive,
	faArrowUp,
	faBan,
	faEnvelope,
	faEnvelopeOpen,
	faHandPointRight,
	faShare,
	faTrashCan,
	faUserEdit,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faEllipsisV } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";

import AppContext from "~/common/AppContext";
import NudgeRequestees from "~/feedback/NudgeRequestees";
import { warning } from "~/lib/prompts";
import { environment } from "~/models";
import type { FeedbackCardType } from "~/nav/panels/Feedback/types";
import { useModals } from "~/ui";
import IconButton from "~/ui/legacy/IconButton";
import Menu from "~/ui/menu";
import toast from "~/ui/toast";

import type { FeedbackCardActionsArchiveMutation } from "~/__generated__/FeedbackCardActionsArchiveMutation.graphql";
import type { FeedbackCardActionsCancelFurtherResponsesMutation } from "~/__generated__/FeedbackCardActionsCancelFurtherResponsesMutation.graphql";
import type { FeedbackCardActionsDeleteDraftMutation } from "~/__generated__/FeedbackCardActionsDeleteDraftMutation.graphql";
import type { FeedbackCardActionsDismissRequestMutation } from "~/__generated__/FeedbackCardActionsDismissRequestMutation.graphql";
import type { FeedbackCardActionsRestoreRequestMutation } from "~/__generated__/FeedbackCardActionsRestoreRequestMutation.graphql";
import type { FeedbackCardActionsToggleReadUnreadMutation } from "~/__generated__/FeedbackCardActionsToggleReadUnreadMutation.graphql";
import type { FeedbackCardActionsUnarchiveMutation } from "~/__generated__/FeedbackCardActionsUnarchiveMutation.graphql";

import styles from "./FeedbackCardActions.module.css";

const toggleReadUnread = graphql`
	mutation FeedbackCardActionsToggleReadUnreadMutation($id: ID!, $value: Boolean!) {
		feedbackToggleReadUnread(id: $id, value: $value) {
			feedbackAndRequestsEdge {
				node {
					... on FeedbackNode {
						id
						lastSeenByUser
					}

					... on FeedbackRequestNode {
						id
						lastSeenByUser
					}
				}
			}
		}
	}
`;

export const restoreRequest = graphql`
	mutation FeedbackCardActionsRestoreRequestMutation($id: ID!) {
		restoreRequest(id: $id) {
			feedbackAndRequestsEdge {
				node {
					... on FeedbackRequestNode {
						id
						isArchived
						cancelledByUser
					}
				}
			}
		}
	}
`;

export const dismissRequest = graphql`
	mutation FeedbackCardActionsDismissRequestMutation($id: ID!) {
		dismissRequest(id: $id) {
			feedbackAndRequestsEdge {
				node {
					... on FeedbackRequestNode {
						id
						isArchived
						cancelledByUser
					}
				}
			}
		}
	}
`;

const deleteDraft = graphql`
	mutation FeedbackCardActionsDeleteDraftMutation($id: ID!) {
		deleteDraftFeedback(id: $id) {
			deletedId @deleteRecord
		}
	}
`;

const unarchive = graphql`
	mutation FeedbackCardActionsUnarchiveMutation($id: ID!) {
		markAsPriority(id: $id) {
			feedbackAndRequestsEdge {
				node {
					... on FeedbackNode {
						id
						isArchived
					}
					... on FeedbackRequestNode {
						id
						isArchived
					}
				}
			}
		}
	}
`;

const archive = graphql`
	mutation FeedbackCardActionsArchiveMutation($id: ID!) {
		markAsComplete(id: $id) {
			feedbackAndRequestsEdge {
				node {
					... on FeedbackNode {
						id
						isArchived
					}
					... on FeedbackRequestNode {
						id
						isArchived
					}
				}
			}
		}
	}
`;

const cancelFurtherResponses = graphql`
	mutation FeedbackCardActionsCancelFurtherResponsesMutation($id: ID!) {
		cancelFurtherResponses(id: $id) {
			feedback {
				id
				requestCount
				recipients {
					user {
						id
					}
				}
			}
		}
	}
`;

interface FeedbackCardActionsProps {
	feedback: FeedbackCardType;
	read?: boolean;
	triggerClassName?: string;
	refetch(): void;
}

export default function FeedbackCardActions({ feedback, read, triggerClassName, refetch }: FeedbackCardActionsProps) {
	const { user } = useContext(AppContext);
	const history = useHistory();
	const modals = useModals();
	const { id } = feedback;
	const isRequest = feedback.__typename === "FeedbackRequestNode";
	const feedbackData = isRequest ? feedback.feedback : feedback;
	const { isDraft, isArchived, isThreeSixtyParent, isAnonymous, type, creator, recipients, context } = feedbackData;
	const contextData = context ? context[0] : null;
	const isUnsolicitedGiven = useMemo(() => {
		return (
			type === "UNSOLICITED" &&
			creator &&
			user &&
			user.id === creator.id &&
			(!recipients || !recipients.some(({ user }) => user.id === creator.id))
		);
	}, [type, creator, user, recipients]);

	const onMenuButtonClick = useCallback((event: React.MouseEvent) => {
		event.stopPropagation();
		event.preventDefault();
	}, []);

	const onMarkAsUnread = useCallback(() => {
		commitMutation<FeedbackCardActionsToggleReadUnreadMutation>(environment, {
			mutation: toggleReadUnread,
			variables: { id, value: false },
			onCompleted: ({ feedbackToggleReadUnread }, err) => {
				if (err?.length || !feedbackToggleReadUnread) {
					toast.error(err?.[0]?.message || "Failed to mark as unread");
				}
			},
			updater: store => {
				const viewer = store.get("viewer");

				if (!viewer) return;

				const count = viewer.getValue("feedbackUnreadItemsCount");
				const newValue = typeof count == "number" ? count + 1 : 1;

				viewer.setValue(newValue, "feedbackUnreadItemsCount");
			},
		});
	}, [id]);

	const onMarkAsRead = useCallback(() => {
		commitMutation<FeedbackCardActionsToggleReadUnreadMutation>(environment, {
			mutation: toggleReadUnread,
			variables: { id, value: true },
			onCompleted: ({ feedbackToggleReadUnread }, err) => {
				if (err?.length || !feedbackToggleReadUnread) {
					toast.error(err?.[0]?.message || "Failed to mark as read");
				}
			},
			updater: store => {
				const viewer = store.get("viewer");

				if (!viewer) return;

				const count = viewer.getValue("feedbackUnreadItemsCount");
				const newValue = typeof count == "number" && count > 0 ? count - 1 : 0;

				viewer.setValue(newValue, "feedbackUnreadItemsCount");
			},
		});
	}, [id]);

	const onRestoreRequest = useCallback(() => {
		commitMutation<FeedbackCardActionsRestoreRequestMutation>(environment, {
			mutation: restoreRequest,
			variables: { id },
			onCompleted: refetch,
		});
	}, [id, refetch]);

	const onDismissRequest = useCallback(() => {
		commitMutation<FeedbackCardActionsDismissRequestMutation>(environment, {
			mutation: dismissRequest,
			variables: { id },
			onCompleted: refetch,
		});
	}, [id, refetch]);

	const onDeleteDraft = useCallback(async () => {
		const response = await warning("Deleting a draft cannot be undone.", {
			yesProps: { variant: "destructive" },
			noProps: { variant: "secondary" },
			yesText: "Delete",
			noText: "No, take me back",
			title: "Delete draft?",
		});

		if (!response) return;

		commitMutation<FeedbackCardActionsDeleteDraftMutation>(environment, {
			mutation: deleteDraft,
			variables: { id },
			onCompleted: (response, err) => {
				if (err) {
					toast.error(`Unable to delete draft: ${err[0].message}`);
					return;
				}

				toast.success("Draft deleted");
			},
			onError: () => toast.error("Unable to delete draft!"),
		});
	}, [id]);

	const onShareWithOthers = useCallback(() => {
		if (isDraft) return;

		if (isThreeSixtyParent) {
			return history.push(`/feedback/three-sixty/${id}/edit/`);
		}

		return history.push(`/feedback/${id}/edit/`, { field: "share" });
	}, [id, isDraft, isThreeSixtyParent, history]);

	const onUnarchive = useCallback(() => {
		commitMutation<FeedbackCardActionsUnarchiveMutation>(environment, {
			mutation: unarchive,
			variables: { id },
		});
	}, [id]);

	const onArchive = useCallback(() => {
		commitMutation<FeedbackCardActionsArchiveMutation>(environment, {
			mutation: archive,
			variables: { id },
			onCompleted: refetch,
		});
	}, [id, refetch]);

	const onRemindRespondents = useCallback(() => {
		modals.open(({ close }) => <NudgeRequestees id={id} onClose={close} selected={isAnonymous} />);
	}, [modals, id, isAnonymous]);

	const onCancelFurtherResponses = useCallback(async () => {
		const response = await warning("Cancelling further responses is a permanent action.", {
			title: "Are you sure?",
			yesProps: { variant: "destructive" },
			noProps: { variant: "secondary" },
			yesText: "Yes",
			noText: "No",
		});

		if (!response) return;

		commitMutation<FeedbackCardActionsCancelFurtherResponsesMutation>(environment, {
			mutation: cancelFurtherResponses,
			variables: { id },
			onCompleted: ({ cancelFurtherResponses }, error) => {
				if (error?.length || !cancelFurtherResponses) {
					toast.error(`Failed to cancel further responses: ${error?.[0].message}`);
				} else {
					toast.success("Successfully cancelled further responses");
				}
			},
		});
	}, [id]);

	const onEditRecipients = useCallback(() => {
		if (isDraft) return;

		if (isThreeSixtyParent) {
			return history.push(`/feedback/three-sixty/${id}/edit/`);
		}

		return history.push(`/feedback/${id}/edit/`, { field: "ask" });
	}, [id, isDraft, isThreeSixtyParent, history]);

	const items = useMemo(() => {
		const markAsUnread = (
			<Menu.Item icon={faEnvelopeOpen} onSelect={onMarkAsUnread}>
				Mark as unread
			</Menu.Item>
		);

		const markAsRead = (
			<Menu.Item icon={faEnvelope} onSelect={onMarkAsRead}>
				Mark as read
			</Menu.Item>
		);

		if (isRequest) {
			if (!feedback.complete && feedback.isArchived) {
				return (
					<Menu.Item icon={faArrowUp} onSelect={onRestoreRequest}>
						Restore request
					</Menu.Item>
				);
			}
			return (
				<>
					{read ? markAsUnread : markAsRead}
					{!feedback.complete && (
						<Menu.Item icon={faArchive} onSelect={onDismissRequest}>
							Archive
						</Menu.Item>
					)}
				</>
			);
		}

		if (isDraft) {
			return (
				<Menu.Item icon={faTrashCan} onSelect={onDeleteDraft}>
					Delete
				</Menu.Item>
			);
		}

		const shareWithOthers = (
			<Menu.Item icon={faShare} onSelect={onShareWithOthers}>
				Share with…
			</Menu.Item>
		);

		if (isArchived) {
			return (
				<>
					{(type === "REQUESTED" || !isUnsolicitedGiven) && (
						<Menu.Item icon={faArchive} onSelect={onUnarchive}>
							Unarchive
						</Menu.Item>
					)}
					{creator && user && creator.id === user.id && type !== "UNSOLICITED" && shareWithOthers}
				</>
			);
		}

		return (
			<>
				{read ? markAsUnread : markAsRead}
				{creator && user && creator.id === user.id && (
					<>
						{feedback.responseCount < feedback.requestCount && (
							<>
								<Menu.Item icon={faHandPointRight} onSelect={onRemindRespondents}>
									Remind respondents…
								</Menu.Item>
								<Menu.Item icon={faBan} onSelect={onCancelFurtherResponses}>
									Cancel further responses
								</Menu.Item>
							</>
						)}
						{type !== "UNSOLICITED" && contextData?.__typename !== "Note" && (
							<>
								<Menu.Item icon={faUserEdit} onSelect={onEditRecipients}>
									Edit recipients…
								</Menu.Item>
								{shareWithOthers}
							</>
						)}
					</>
				)}
				<Menu.Item icon={faArchive} onSelect={onArchive}>
					Archive
				</Menu.Item>
			</>
		);
	}, [
		isRequest,
		feedback,
		isDraft,
		isArchived,
		type,
		isUnsolicitedGiven,
		creator,
		user,
		read,
		onArchive,
		onCancelFurtherResponses,
		onDeleteDraft,
		onDismissRequest,
		onEditRecipients,
		onMarkAsUnread,
		onMarkAsRead,
		onRemindRespondents,
		onRestoreRequest,
		onShareWithOthers,
		onUnarchive,
		contextData,
	]);

	return (
		<Menu>
			<Menu.Trigger>
				<IconButton
					title="Options"
					data-test-id="feedback-actions-button"
					className={cx(styles.trigger, triggerClassName)}
					icon={faEllipsisV}
					onClick={onMenuButtonClick}
				/>
			</Menu.Trigger>
			<Menu.Content onClick={onMenuButtonClick}>{items}</Menu.Content>
		</Menu>
	);
}
